// auth-compatible adapter hooks backed by vortex-auth.
//
// Purpose: F3 swaps ~24 auth call sites by changing the IMPORT only. Each hook
// returns an object whose field names match what the existing auth call sites
// destructure, so the component bodies don't need to change.
//
// Coverage (per Stage F1 plan — the minimal surface the data routes read):
//   useActiveOrganization() -> { organization: { id, name } | null, isLoaded }
//   useCurrentUser()        -> { user | null, isLoaded }
//   useAuthActions()        -> { signOut }
//
// Richer auth org surfaces (memberships, invitations, inviteMember, update, etc.)
// are intentionally NOT covered here — those call sites migrate to dedicated
// vortex-auth org components in F2/F3, not via this adapter.
import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useQuery } from "@tanstack/react-query";

import { organizationsGetActive } from "~/api-refs";

import { useAppAuthActions, useAppUser } from "./runtime";

/**
 * Auth organization hook replacement.
 *
 * Backed by `organizations.getActive`, which returns plasma's organization
 * document for the user's `activeVortexOrgId`. We map the doc's identity fields
 * to the auth organization shape that call sites read:
 *   - `id`   <- `organizationId` (plasma's legacy primary-key string)
 *   - `name` <- `businessName`
 *
 * IMPORTANT — why `id` is the legacy `organizationId`, not `vortexOrgId`:
 * The portal's data backends (Stripe Connect actions, AI `organizationId` queries)
 * resolve organizations via the `by_organization_id` index on the legacy
 * `organizationId` string — NOT `vortexOrgId` or the Convex doc `_id`. Call
 * sites pass `organization.id` straight into `organizationId` args, so the
 * adapter MUST surface the legacy id for those lookups to hit.
 * Call sites that need the Convex doc `_id` (tax-rate sync, AI context/thread
 * mutations) use `useActiveOrganizationConvexId()` below instead.
 */
export function useActiveOrganization(): {
  organization: { id: string; name: string } | null;
  isLoaded: boolean;
} {
  const { data, isPending } = useQuery(convexQuery(organizationsGetActive, {}));

  if (isPending) {
    return { organization: null, isLoaded: false };
  }

  if (!data) {
    return { organization: null, isLoaded: true };
  }

  return {
    organization: {
      id: data.organizationId,
      name: data.businessName ?? "",
    },
    isLoaded: true,
  };
}

/**
 * Returns the active organization's Convex document id (`Id<"organizations">`).
 *
 * Some backends take the actual Convex table id (`v.string()` cast to
 * `Id<"organizations">`) rather than the legacy `organizationId` string —
 * specifically the AI context/thread mutations and `stripeTax:
 * manualSyncTaxCategoryRates`. Those call sites use THIS hook, while everything
 * keyed on the legacy string id uses `useActiveOrganization()`.
 */
export function useActiveOrganizationConvexId(): {
  organizationId: Id<"organizations"> | null;
  isLoaded: boolean;
} {
  const { data, isPending } = useQuery(convexQuery(organizationsGetActive, {}));

  if (isPending) {
    return { organizationId: null, isLoaded: false };
  }

  return {
    organizationId: data?._id ?? null,
    isLoaded: true,
  };
}

/**
 * Returns the active organization's `vortexOrgId` (the vortex-auth tenant id).
 *
 * The `VortexOrganizationMembersSurface` passes its `organizationId` prop
 * straight into `organizations.inviteMember({ organizationId })`, whose
 * authorize step requires `organizationId === org.vortexAuthOrganizationId` (NOT the legacy
 * `organizationId` string surfaced by `useActiveOrganization()`). So the
 * members surface MUST receive `vortexOrgId`. This hook provides exactly that.
 */
export function useActiveOrganizationVortexId(): {
  vortexOrgId: string | null;
  isLoaded: boolean;
} {
  const { data, isPending } = useQuery(convexQuery(organizationsGetActive, {}));

  if (isPending) {
    return { vortexOrgId: null, isLoaded: false };
  }

  return {
    vortexOrgId: data?.vortexAuthOrganizationId ?? null,
    isLoaded: true,
  };
}

/**
 * Auth user hook replacement.
 *
 * Backed by the vortex-auth runtime `useAppUser()` (which wraps
 * `useVortexAuthUser`). Returns the runtime user shape directly — it already
 * exposes auth-compatible fields (`id`, `fullName`, `username`,
 * `primaryEmailAddress.emailAddress`).
 */
export function useCurrentUser(): ReturnType<typeof useAppUser> {
  return useAppUser();
}

/**
 * Auth sign-out hook replacement.
 *
 * Backed by the vortex-auth runtime `useAppAuthActions()` (crm-proven path).
 * Matches the `{ signOut }` shape call sites use for sign-out actions.
 */
export function useAuthActions(): {
  signOut: () => Promise<void>;
} {
  const actions = useAppAuthActions();
  return {
    signOut: async () => {
      await actions.signOut();
    },
  };
}
